import React, { useEffect, useMemo, useState } from "react";
import { Check, RefreshCw, Search, Wrench, X } from "lucide-react";

async function apiGet(url) {
  const r = await fetch(url, { credentials: "include" });
  const j = await r.json().catch(() => null);
  if (!j || j.ok !== true) throw new Error(j?.error || "Errore API");
  return j;
}

async function apiPost(url, body) {
  const r = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const j = await r.json().catch(() => null);
  if (!j || j.ok !== true) throw new Error(j?.error || "Errore API");
  return j;
}

const FREQUENCY_OPTIONS = [
  { value: "daily", label: "Giornaliera" },
  { value: "weekly", label: "Settimanale" },
  { value: "biweekly", label: "Bisettimanale" },
  { value: "monthly", label: "Mensile" },
];

function statusBadge(status) {
  if (status === "approved") return "bg-emerald-100 text-emerald-700";
  if (status === "rejected") return "bg-red-100 text-red-700";
  return "bg-amber-100 text-amber-800";
}

function statusLabel(status) {
  if (status === "approved") return "Approvata";
  if (status === "rejected") return "Rifiutata";
  return "In attesa";
}

export default function AttrezzatureProposteAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [status, setStatus] = useState("pending");
  const [query, setQuery] = useState("");
  const [reviewingId, setReviewingId] = useState(null);
  const [assignToCompany, setAssignToCompany] = useState({});
  const [frequencyById, setFrequencyById] = useState({});

  async function loadItems() {
    setLoading(true);
    setErr("");
    try {
      const params = new URLSearchParams();
      params.set("status", status);
      if (query.trim()) params.set("q", query.trim());

      const data = await apiGet(`/api/admin/attrezzature_proposte_list.php?${params.toString()}`);
      setItems(Array.isArray(data.items) ? data.items : []);
    } catch (e) {
      setErr(e.message || "Errore caricamento");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const pendingCount = useMemo(
    () => items.filter((item) => item.status === "pending").length,
    [items]
  );

  async function approve(item) {
    try {
      setReviewingId(item.id);
      await apiPost("/api/admin/attrezzature_proposte_approve.php", {
        id: item.id,
        assign_to_company: !!assignToCompany[item.id],
        frequency: frequencyById[item.id] || "daily",
      });
      await loadItems();
    } catch (e) {
      alert(e.message || "Errore approvazione");
    } finally {
      setReviewingId(null);
    }
  }

  async function reject(item) {
    const reason = window.prompt("Motivazione del rifiuto (opzionale):", "") || "";
    try {
      setReviewingId(item.id);
      await apiPost("/api/admin/attrezzature_proposte_reject.php", {
        id: item.id,
        reason,
      });
      await loadItems();
    } catch (e) {
      alert(e.message || "Errore rifiuto");
    } finally {
      setReviewingId(null);
    }
  }

  return (
    <div className="bg-white shadow-md rounded-2xl p-4 md:p-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Wrench size={20} /> Richieste attrezzature
          </div>
          <div className="text-sm text-slate-500">
            Solo superadmin: verifica, approvazione o rifiuto delle attrezzature proposte dai clienti.
          </div>
        </div>

        <div className="flex items-center gap-2">
          {status === "pending" && (
            <div className="px-3 py-2 rounded-xl bg-amber-50 text-amber-800 text-sm font-medium">
              {pendingCount} in attesa
            </div>
          )}
          <button
            onClick={loadItems}
            className="px-3 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 flex items-center gap-2"
          >
            <RefreshCw size={16} /> Aggiorna
          </button>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-12 gap-3">
        <div className="md:col-span-3">
          <label className="text-xs text-slate-600">Stato</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="w-full mt-1 rounded-xl border border-slate-200 px-3 py-2"
          >
            <option value="pending">In attesa</option>
            <option value="approved">Approvate</option>
            <option value="rejected">Rifiutate</option>
            <option value="all">Tutte</option>
          </select>
        </div>

        <div className="md:col-span-7">
          <label className="text-xs text-slate-600">Cerca</label>
          <div className="mt-1 flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2">
            <Search size={16} className="text-slate-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Attrezzatura, azienda o utente..."
              className="w-full outline-none"
            />
          </div>
        </div>

        <div className="md:col-span-2 flex items-end">
          <button
            onClick={loadItems}
            className="w-full px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            Filtra
          </button>
        </div>
      </div>

      {err && (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {err}
        </div>
      )}

      <div className="mt-4 space-y-4">
        {loading ? (
          <div className="rounded-2xl border border-slate-200 p-5 text-slate-500">Caricamento...</div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 p-5 text-slate-500">
            Nessuna richiesta trovata.
          </div>
        ) : (
          items.map((item) => {
            const busy = reviewingId === item.id;
            const assignChecked = !!assignToCompany[item.id];
            const selectedFrequency = frequencyById[item.id] || "daily";

            return (
              <div key={item.id} className="rounded-2xl border border-slate-200 p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="text-lg font-semibold text-slate-900">{item.nome}</div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusBadge(item.status)}`}>
                        {statusLabel(item.status)}
                      </span>
                      {item.categoria ? (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                          {item.categoria}
                        </span>
                      ) : null}
                    </div>
                    <div className="mt-1 text-sm text-slate-500">
                      Azienda: <span className="font-medium text-slate-700">{item.azienda_nome || `#${item.azienda_id}`}</span>
                      {" · "}
                      Richiesta da: <span className="font-medium text-slate-700">{item.created_by_nome || item.created_by_email || `#${item.created_by}`}</span>
                      {" · "}
                      {item.created_at}
                    </div>
                  </div>

                  {item.status === "pending" ? (
                    <div className="flex items-center gap-2">
                      <button
                        disabled={busy}
                        onClick={() => reject(item)}
                        className="px-3 py-2 rounded-xl bg-red-600 hover:bg-red-700 text-white inline-flex items-center gap-2 disabled:opacity-50"
                      >
                        <X size={16} /> Rifiuta
                      </button>
                      <button
                        disabled={busy}
                        onClick={() => approve(item)}
                        className="px-3 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center gap-2 disabled:opacity-50"
                      >
                        <Check size={16} /> Approva
                      </button>
                    </div>
                  ) : null}
                </div>

                {item.note ? (
                  <div className="mt-3 rounded-xl bg-slate-50 border border-slate-200 p-3 text-sm text-slate-700">
                    {item.note}
                  </div>
                ) : null}

                {item.status === "pending" ? (
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-12 gap-3">
                    <label className="md:col-span-5 flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2">
                      <input
                        type="checkbox"
                        checked={assignChecked}
                        onChange={(e) =>
                          setAssignToCompany((prev) => ({ ...prev, [item.id]: e.target.checked }))
                        }
                      />
                      <span className="text-sm text-slate-700">
                        Assegna subito anche all'azienda richiedente
                      </span>
                    </label>

                    <div className="md:col-span-3">
                      <label className="text-xs text-slate-600">Frequenza iniziale</label>
                      <select
                        value={selectedFrequency}
                        onChange={(e) =>
                          setFrequencyById((prev) => ({ ...prev, [item.id]: e.target.value }))
                        }
                        className="w-full mt-1 rounded-xl border border-slate-200 px-3 py-2"
                        disabled={!assignChecked}
                      >
                        {FREQUENCY_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                ) : (
                  <div className="mt-4 text-sm text-slate-500">
                    {item.status === "approved" ? (
                      <>
                        Approvata da <span className="font-medium text-slate-700">{item.reviewed_by_nome || `#${item.reviewed_by}`}</span>
                        {" · "}
                        {item.reviewed_at}
                        {item.approved_attrezzatura_nome ? (
                          <>
                            {" · "}
                            Catalogo: <span className="font-medium text-slate-700">{item.approved_attrezzatura_nome}</span>
                          </>
                        ) : null}
                      </>
                    ) : (
                      <>
                        Rifiutata da <span className="font-medium text-slate-700">{item.reviewed_by_nome || `#${item.reviewed_by}`}</span>
                        {" · "}
                        {item.reviewed_at}
                        {item.reject_reason ? (
                          <>
                            {" · "}
                            Motivo: <span className="font-medium text-slate-700">{item.reject_reason}</span>
                          </>
                        ) : null}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
